import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ReferenceDataManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('hs_code');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ code: '', description: '' });
  const [showGlobalData, setShowGlobalData] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 15;

  const tabs = [
    { id: 'hs_code', name: 'HS Codes', icon: '📦' },
    { id: 'uom', name: 'UOM Codes', icon: '📏' },
    { id: 'registration_type', name: 'Registration Types', icon: '🆔' },
    { id: 'invoice_type', name: 'Invoice Types', icon: '📄' },
    { id: 'document_types', name: 'Document Types', icon: '📋', fbr: true },
    { id: 'sro_items', name: 'SRO Items', icon: '🏷️', fbr: true },
    { id: 'transaction_types', name: 'Transaction Types', icon: '💱', fbr: true },
    { id: 'statl_check', name: 'STATL Check', icon: '✅', fbr: true, special: true }
  ];

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when tab changes
    loadData(1);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when page changes
  useEffect(() => {
    if (currentPage > 1) {
      loadData(currentPage);
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async (page = 1) => {
    try {
      setLoading(true);

      // Map tab names to actual API endpoints
      const endpointMap = {
        'hs_code': 'hs-codes',
        'uom': 'uom',
        'registration_type': 'registration-types',
        'invoice_type': 'invoice-types',
        'document_types': 'document-types',
        'sro_items': 'sro-items',
        'transaction_types': 'transaction-types'
      };

      const endpoint = endpointMap[activeTab] || activeTab;

      // Handle special STATL check tab
      if (activeTab === 'statl_check') {
        setData([]);
        setTotalPages(1);
        setTotalRecords(0);
        setCurrentPage(1);
        return;
      }

      // Add pagination parameters
      const params = {
        page: page,
        limit: itemsPerPage
      };

      const response = await apiClient.get(`/reference/${endpoint}`, { params });

      // Handle different response formats
      const responseData = response.data;
      if (responseData.data && Array.isArray(responseData.data)) {
        // Check if it's a paginated response
        if (responseData.pagination) {
          // Backend paginated response
          setData(responseData.data);
          setTotalPages(responseData.pagination.totalPages || responseData.pagination.pages);
          setTotalRecords(responseData.pagination.total);
          setCurrentPage(responseData.pagination.page);
        } else {
          // Non-paginated response - implement client-side pagination
          const allData = responseData.data;
          const startIndex = (page - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          const paginatedData = allData.slice(startIndex, endIndex);

          setData(paginatedData);
          setTotalPages(Math.ceil(allData.length / itemsPerPage));
          setTotalRecords(allData.length);
          setCurrentPage(page);
        }
      } else if (Array.isArray(responseData)) {
        // Direct array response - implement client-side pagination
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = responseData.slice(startIndex, endIndex);

        setData(paginatedData);
        setTotalPages(Math.ceil(responseData.length / itemsPerPage));
        setTotalRecords(responseData.length);
        setCurrentPage(page);
      } else {
        // Fallback
        setData([]);
        setTotalPages(1);
        setTotalRecords(0);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code.trim() || !formData.description.trim()) {
      toast.error('Code and description are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        source: editingItem?.source || (user.role === 'admin' && showGlobalData ? 'global' : 'user')
      };

      if (editingItem) {
        // Update existing item
        await apiClient.put(`/reference/${activeTab}/${editingItem.id}`, payload);
        toast.success(`${editingItem.source === 'global' ? 'Global' : 'User-specific'} item updated successfully`);
      } else {
        // Create new item
        await apiClient.post(`/reference/${activeTab}`, payload);
        toast.success(`${payload.source === 'global' ? 'Global' : 'User-specific'} item created successfully`);
      }

      setShowModal(false);
      setEditingItem(null);
      setFormData({ code: '', description: '' });
      loadData(currentPage); // Reload current page
    } catch (error) {
      console.error('Failed to save item:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save item';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ code: item.code, description: item.description });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    const itemType = item.source === 'global' ? 'global' : 'user-specific';
    if (!window.confirm(`Are you sure you want to delete this ${itemType} "${item.code}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/reference/${activeTab}/${item.id}`, {
        data: { source: item.source }
      });
      toast.success(`${itemType} item deleted successfully`);

      // If this was the last item on the current page and we're not on page 1, go to previous page
      if (data.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        loadData(currentPage);
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete item';
      toast.error(errorMessage);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ code: '', description: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ code: '', description: '' });
  };

  const currentTab = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reference Data Management</h1>
              <p className="text-sm text-gray-600 mt-1">
                {user.role === 'admin' ? 'Admin: Manage global and user-specific data' : 'User: Manage your custom data'}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {user.role === 'admin' && (
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">View:</label>
                  <select
                    value={showGlobalData ? 'global' : 'user'}
                    onChange={(e) => setShowGlobalData(e.target.value === 'global')}
                    className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                  >
                    <option value="global">Global Data</option>
                    <option value="user">All Data</option>
                  </select>
                </div>
              )}
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add New {currentTab?.name.slice(0, -1)}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Header with pagination info */}
          {!loading && activeTab !== 'statl_check' && (
            <div className="mb-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {tabs.find(tab => tab.id === activeTab)?.name || 'Reference Data'}
                </h3>
                <p className="text-sm text-gray-600">
                  {totalRecords > 0 ? (
                    <>Showing {itemsPerPage} records per page • Total: {totalRecords} records</>
                  ) : (
                    'No records found'
                  )}
                </p>
              </div>
              {user.role === 'admin' && activeTab !== 'document_types' && activeTab !== 'sro_items' && activeTab !== 'transaction_types' && (
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showGlobalData}
                      onChange={(e) => setShowGlobalData(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-600">Show global data</span>
                  </label>
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add New
                  </button>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : activeTab === 'statl_check' ? (
            <STATLCheckComponent />
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                        No {currentTab?.name.toLowerCase()} found
                      </td>
                    </tr>
                  ) : (
                    data.map((item) => (
                      <tr key={`${item.source}-${item.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.code}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {item.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.source === 'global'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {item.source === 'global' ? 'Global' : 'User'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {/* Users can edit their own data, admins can edit all data */}
                          {(item.source === 'user' || user.role === 'admin') && (
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              Edit
                            </button>
                          )}
                          {/* Users can delete their own data, admins can delete all data */}
                          {(item.source === 'user' || user.role === 'admin') && (
                            <button
                              onClick={() => handleDelete(item)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          )}
                          {/* Show read-only indicator for global data when user is not admin */}
                          {item.source === 'global' && user.role !== 'admin' && (
                            <span className="text-gray-400 text-xs">Read-only</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * itemsPerPage, totalRecords)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{totalRecords}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {/* Page Numbers */}
                      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 7) {
                          pageNum = i + 1;
                        } else if (currentPage <= 4) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 3) {
                          pageNum = totalPages - 6 + i;
                        } else {
                          pageNum = currentPage - 3 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingItem ? 'Edit' : 'Add New'} {currentTab?.name.slice(0, -1)}
              </h3>

              {/* Scope indicator */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  {editingItem ? (
                    <>Editing <strong>{editingItem.source === 'global' ? 'global' : 'user-specific'}</strong> data</>
                  ) : (
                    <>Creating <strong>{user.role === 'admin' && showGlobalData ? 'global' : 'user-specific'}</strong> data</>
                  )}
                  {user.role === 'admin' && !editingItem && (
                    <span className="block text-xs text-gray-500 mt-1">
                      Use the view selector above to choose between global and user-specific data creation
                    </span>
                  )}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                    Code *
                  </label>
                  <input
                    type="text"
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingItem ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// STATL Check Component
const STATLCheckComponent = () => {
  const [statlForm, setStatlForm] = useState({ regno: '', date: '' });
  const [regTypeForm, setRegTypeForm] = useState({ registration_no: '' });
  const [statlResult, setStatlResult] = useState(null);
  const [regTypeResult, setRegTypeResult] = useState(null);
  const [statlValidation, setStatlValidation] = useState(null);
  const [regTypeValidation, setRegTypeValidation] = useState(null);
  const [checking, setChecking] = useState(false);

  const handleSTATLCheck = async (e) => {
    e.preventDefault();
    if (!statlForm.regno || !statlForm.date) {
      toast.error('Registration number and date are required');
      return;
    }

    setChecking(true);
    try {
      const response = await apiClient.post('/reference/validate-statl', statlForm);
      setStatlResult(response.data.data);
      setStatlValidation(response.data.validation);
      
      // Show validation result with corrected status interpretation
      const validation = response.data.validation;
      const statusMessage = validation.isValid ? 
        `✅ Registration is ${validation.status}` : 
        `❌ Registration is ${validation.status}`;
      
      toast.success(statusMessage);
    } catch (error) {
      console.error('STATL check failed:', error);
      toast.error('STATL check failed: ' + error.message);
    } finally {
      setChecking(false);
    }
  };

  const handleRegTypeCheck = async (e) => {
    e.preventDefault();
    if (!regTypeForm.registration_no) {
      toast.error('Registration number is required');
      return;
    }

    setChecking(true);
    try {
      const response = await apiClient.post('/reference/validate-registration-type', regTypeForm);
      setRegTypeResult(response.data.data);
      setRegTypeValidation(response.data.validation);
      
      // Show validation result with corrected registration type interpretation
      const validation = response.data.validation;
      const typeMessage = validation.isValid ? 
        `✅ Registration type: ${validation.registrationType}` : 
        `❌ Registration type: ${validation.registrationType}`;
      
      toast.success(typeMessage);
    } catch (error) {
      console.error('Registration type check failed:', error);
      toast.error('Registration type check failed: ' + error.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* STATL Status Check */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">STATL Status Check</h3>
        <form onSubmit={handleSTATLCheck} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Registration Number</label>
              <input
                type="text"
                value={statlForm.regno}
                onChange={(e) => setStatlForm(prev => ({ ...prev, regno: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter registration number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <input
                type="date"
                value={statlForm.date}
                onChange={(e) => setStatlForm(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={checking}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {checking ? 'Checking...' : 'Check STATL Status'}
          </button>
        </form>

        {statlValidation && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h4 className="font-medium text-gray-900">STATL Status Result:</h4>
            <div className="mt-3 space-y-2">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  statlValidation.isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {statlValidation.isValid ? '✅ Active' : '❌ Inactive'}
                </span>
                <span className="text-sm text-gray-600">Status: {statlValidation.status}</span>
              </div>
              <div className="text-sm text-gray-600">
                <p><strong>Registration Number:</strong> {statlValidation.regno}</p>
                <p><strong>Date:</strong> {statlValidation.date}</p>
                <p><strong>Status Code:</strong> {statlValidation.statusCode}</p>
                {statlValidation.note && (
                  <p className="text-xs text-blue-600 mt-2"><strong>Note:</strong> {statlValidation.note}</p>
                )}
              </div>
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Show Raw FBR Response
                </summary>
                <pre className="mt-2 text-xs text-gray-600 bg-white p-2 rounded border overflow-x-auto">
                  {JSON.stringify(statlResult, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        )}
      </div>

      {/* Registration Type Check */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Registration Type Check</h3>
        <form onSubmit={handleRegTypeCheck} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Registration Number</label>
            <input
              type="text"
              value={regTypeForm.registration_no}
              onChange={(e) => setRegTypeForm(prev => ({ ...prev, registration_no: e.target.value }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter registration number"
            />
          </div>
          <button
            type="submit"
            disabled={checking}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {checking ? 'Checking...' : 'Check Registration Type'}
          </button>
        </form>

        {regTypeValidation && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h4 className="font-medium text-gray-900">Registration Type Result:</h4>
            <div className="mt-3 space-y-2">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  regTypeValidation.isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {regTypeValidation.isValid ? '✅ Registered' : '❌ Unregistered'}
                </span>
                <span className="text-sm text-gray-600">Type: {regTypeValidation.registrationType}</span>
              </div>
              <div className="text-sm text-gray-600">
                <p><strong>Registration Number:</strong> {regTypeValidation.registrationNo}</p>
                <p><strong>Status Code:</strong> {regTypeValidation.statusCode}</p>
                {regTypeValidation.note && (
                  <p className="text-xs text-blue-600 mt-2"><strong>Note:</strong> {regTypeValidation.note}</p>
                )}
              </div>
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Show Raw FBR Response
                </summary>
                <pre className="mt-2 text-xs text-gray-600 bg-white p-2 rounded border overflow-x-auto">
                  {JSON.stringify(regTypeResult, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceDataManagement;
